<template>
    <form ref="teamForm" class="flex flex-col gap-6" @submit.prevent="createTeam">
        <BaseButton
            v-if="recommendationsUrl && showDetails"
            theme="tertiary"
            class="body-small text-ink-muted self-start p-0 hover:bg-transparent hover:underline"
            :disabled="submitting"
            @click="backToSuggestions">
            <i class="bi bi-chevron-left" aria-hidden="true" />{{ t("peopleDirectory.createTeam.backToSuggestions") }}
        </BaseButton>
        <BaseLink v-else :href="backUrl" type="button" class="body-small text-ink-muted self-start hover:underline">
            <i class="bi bi-chevron-left" aria-hidden="true" />{{ t("peopleDirectory.createTeam.back") }}
        </BaseLink>
        <header class="flex flex-col gap-2">
            <h1 class="display-serif text-ink">{{ recommendationsUrl && showDetails ? t("peopleDirectory.createTeam.detailsTitle") : t("peopleDirectory.createTeam.title") }}</h1>
            <p class="body text-ink-muted">{{ recommendationsUrl && showDetails ? t("peopleDirectory.createTeam.detailsHint") : t("peopleDirectory.createTeam.intro") }}</p>
        </header>
        <section
            v-if="recommendationsUrl"
            v-show="!showDetails"
            ref="suggestionsScreen"
            tabindex="-1"
            class="flex flex-col gap-6"
            :aria-label="t('peopleDirectory.teamSuggestions.title')">
            <CalendarTeamSuggestions
                :url="recommendationsUrl"
                :demo-tools-visible="!showDetails"
                :selected-member-ids="[creator.id, ...members.map((member) => member.id)]"
                :selected-unavailable-ids="unavailableMembers.map((member) => member.id)"
                :disabled="submitting || members.length >= 100"
                @select="useSuggestion"
                @review="reviewTeam" />
            <div class="flex flex-wrap justify-end gap-2 border-t border-border pt-5">
                <BaseButton theme="secondary" @click="reviewTeam">{{ t("peopleDirectory.createTeam.manual") }}</BaseButton>
            </div>
        </section>
        <template v-if="showDetails">
            <section class="flex flex-col gap-4" :aria-label="t('peopleDirectory.createTeam.detailsTitle')">
                <FormField id="team-name" :label="t('peopleDirectory.createTeam.name')" required>
                    <BaseInput v-model="name" :placeholder="t('peopleDirectory.createTeam.namePlaceholder')" :maxlength="255" :disabled="submitting" required />
                </FormField>
                <section class="flex flex-col gap-3" :aria-label="t('peopleDirectory.createTeam.members')">
                    <FormField id="team-member-search" :label="t('peopleDirectory.createTeam.searchLabel')" :hint="t('peopleDirectory.createTeam.searchHint')">
                        <BaseCombobox
                            v-model="candidate"
                            v-model:input="query"
                            :clearable="false"
                            :ignore-filter="true"
                            :options="options"
                            :key-value="(person) => person.id"
                            :display-value="(person) => `${person.name} ${person.email}`"
                            :placeholder="t('peopleDirectory.createTeam.searchPlaceholder')"
                            :disabled="submitting || members.length >= 100"
                            @keydown.enter.prevent>
                            <template #item="{ option }">
                                <div class="flex items-center gap-3 fs-mask">
                                    <PeopleAvatar :name="option.name" size="sm" />
                                    <div class="min-w-0">
                                        <p class="body-small-semibold text-ink truncate">{{ option.name }}</p>
                                        <p class="details text-ink-muted truncate">{{ option.email }}</p>
                                    </div>
                                </div>
                            </template>
                            <template #empty
                                ><p class="body-small p-3 text-ink-muted">{{ searchStatus }}</p></template
                            >
                        </BaseCombobox>
                    </FormField>
                    <p class="details text-ink-muted empty:sr-only" role="status">
                        <template v-if="searchStatus">{{ searchStatus }}</template>
                    </p>
                    <div class="empty:sr-only" role="alert">
                        <template v-if="searchError">
                            <p class="body-small text-ink">{{ t("peopleDirectory.createTeam.searchError") }}</p>
                            <BaseButton theme="secondary" size="sm" class="mt-2" :disabled="isFetching" @click="execute()">
                                {{ t("peopleDirectory.retry") }}
                            </BaseButton>
                        </template>
                    </div>
                    <BaseCard padding="none" radius="lgr">
                        <div class="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
                            <h2 class="body-small-semibold text-ink">{{ t("peopleDirectory.createTeam.members") }}</h2>
                            <p class="details text-ink-muted" role="status">{{ t("peopleDirectory.createTeam.memberCount", { count: members.length + 1 }) }}</p>
                        </div>
                        <ul class="divide-y divide-border">
                            <li class="flex items-center gap-3 p-4">
                                <PeopleAvatar :name="creator.name" size="sm" />
                                <div class="min-w-0 flex-1">
                                    <p class="body-small-semibold text-ink truncate">{{ t("peopleDirectory.createTeam.you", { name: creator.name }) }}</p>
                                    <p class="details text-ink-muted truncate">{{ creator.email }}</p>
                                </div>
                                <BaseChip size="xs">{{ t("peopleDirectory.createTeam.lead") }}</BaseChip>
                            </li>
                            <li v-for="member in members" :key="member.id" class="flex items-center gap-3 p-4">
                                <PeopleAvatar :name="member.name" size="sm" />
                                <div class="min-w-0 flex-1">
                                    <p class="body-small-semibold text-ink truncate">{{ member.name }}</p>
                                    <p class="details text-ink-muted truncate">{{ member.email }}</p>
                                </div>
                                <BaseButton
                                    theme="neutral"
                                    size="sm"
                                    icon-only
                                    :aria-label="t('peopleDirectory.createTeam.remove', { name: member.name })"
                                    :disabled="submitting"
                                    @click="removeMember(member.id)">
                                    <i class="bi bi-x-lg" aria-hidden="true" />
                                </BaseButton>
                            </li>
                        </ul>
                    </BaseCard>
                    <BaseCard v-if="unavailableMembers.length" padding="sm" radius="lgr" theme="muted">
                        <section class="flex flex-col gap-3" :aria-label="t('peopleDirectory.createTeam.unavailableTitle')">
                            <h2 class="body-small-semibold text-ink">{{ t("peopleDirectory.createTeam.unavailableTitle") }}</h2>
                            <p class="body-small text-ink-muted">{{ t("peopleDirectory.createTeam.unavailableHint") }}</p>
                            <ul class="flex flex-col gap-3">
                                <li v-for="member in unavailableMembers" :key="member.id" class="flex items-center gap-3">
                                    <PeopleAvatar :name="member.name" size="sm" />
                                    <div class="min-w-0 flex-1">
                                        <p class="body-small-semibold text-ink break-words">{{ member.name }}</p>
                                        <p class="details text-ink-muted break-words">{{ member.email }}</p>
                                        <p class="details text-ink-muted">{{ t("peopleDirectory.teamSuggestions.unavailable") }}</p>
                                    </div>
                                    <BaseButton
                                        theme="neutral"
                                        size="sm"
                                        icon-only
                                        :aria-label="t('peopleDirectory.createTeam.remove', { name: member.name })"
                                        :disabled="submitting"
                                        @click="unavailableMembers = unavailableMembers.filter((person) => person.id !== member.id)">
                                        <i class="bi bi-x-lg" aria-hidden="true" />
                                    </BaseButton>
                                </li>
                            </ul>
                        </section>
                    </BaseCard>
                    <p class="body-small text-ink-muted flex items-start gap-2">
                        <i class="bi bi-shield-check shrink-0" aria-hidden="true" />{{ t("peopleDirectory.createTeam.membersHint") }}
                    </p>
                </section>
            </section>
            <BaseNotification theme="error" hide-dismiss class="empty:sr-only">
                <template #content>
                    <div v-if="submitError" class="flex flex-col gap-2">
                        <p class="body-small-semibold text-ink">{{ t("peopleDirectory.createTeam.errorTitle") }}</p>
                        <p class="body-small text-ink">{{ submitError }}</p>
                        <p class="body-small text-ink-muted">{{ t("peopleDirectory.createTeam.errorHint") }}</p>
                    </div>
                </template>
            </BaseNotification>
            <div class="flex flex-col gap-2 empty:sr-only" role="status">
                <template v-if="createdTeam">
                    <p class="body text-ink">{{ t("peopleDirectory.createTeam.created", { name: createdTeam.name }) }}</p>
                    <BaseLink :href="createdTeam.url" class="body-small text-ink underline">{{ t("peopleDirectory.createTeam.openTeam") }}</BaseLink>
                </template>
            </div>
            <footer v-if="!createdTeam" class="flex flex-col gap-4 border-t border-border pt-5 md:flex-row md:items-center md:justify-between">
                <p class="body-small text-ink-muted" role="status">
                    {{
                        submitting
                            ? t("peopleDirectory.createTeam.creatingStatus")
                            : submitError
                              ? t("peopleDirectory.createTeam.retryHint")
                              : canCreate
                                ? t("peopleDirectory.createTeam.ready")
                                : t("peopleDirectory.createTeam.requirements")
                    }}
                </p>
                <div class="flex justify-end gap-2">
                    <BaseButton theme="primary" type="submit" :disabled="!canCreate">{{
                        submitting ? t("peopleDirectory.createTeam.creating") : t("peopleDirectory.createTeam.submit")
                    }}</BaseButton>
                </div>
            </footer>
        </template>
    </form>
</template>

<script setup lang="ts">
import { refDebounced, type UseFetchReturn } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type {
    CalendarTeamMember,
    TeamCandidate,
    TeamCandidateSearch,
    TeamCreateRequest,
    TeamPerspectiveSummary,
    TeamRecommendation,
} from "~vue/api/generated/team-perspective/types.gen";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseCombobox from "~vue/components/BaseCombobox.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import FormField from "~vue/components/form/FormField.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import CalendarTeamSuggestions from "~vue/Connections/CalendarTeamSuggestions.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { computed, nextTick, ref, shallowRef, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

const {
    creator,
    searchUrl,
    createUrl,
    backUrl,
    recommendationsUrl = null,
} = defineProps<{
    creator: TeamCandidate;
    searchUrl: string;
    createUrl: string;
    backUrl: string;
    recommendationsUrl?: string | null;
}>();
const emit = defineEmits<{ created: [team: TeamPerspectiveSummary] }>();
const { t } = useI18n();
const teamForm = useTemplateRef<HTMLFormElement>("teamForm");
const name = ref("");
const showDetails = ref(!recommendationsUrl);
const suggestionsScreen = useTemplateRef<HTMLElement>("suggestionsScreen");

async function backToSuggestions() {
    showDetails.value = false;
    await nextTick();
    suggestionsScreen.value?.focus();
}

async function reviewTeam() {
    showDetails.value = true;
    await nextTick();
    const input = teamForm.value?.querySelector<HTMLInputElement>("#team-name");
    input?.focus({ preventScroll: true });
    input?.scrollIntoView({ block: "center", behavior: "instant" });
}
const members = ref<TeamCandidate[]>([]);
const unavailableMembers = ref<CalendarTeamMember[]>([]);
const candidate = ref<TeamCandidate | null>(null);
const query = ref("");
const debouncedQuery = refDebounced(query, 250);
const url = computed(() => `${searchUrl}?${new URLSearchParams({ q: debouncedQuery.value.trim() })}`);
const searchRequest = shallowRef<UseFetchReturn<TeamCandidateSearch>>();
const data = computed(() => searchRequest.value?.data.value);
const searchError = computed(() => searchRequest.value?.error.value);
const isFetching = computed(() => searchRequest.value?.isFetching.value ?? false);
watch(url, (search, _previous, onCleanup) => {
    searchRequest.value = undefined;
    if (debouncedQuery.value.trim().length < 2) {
        return;
    }
    // Keep each request's state separate so superseded responses cannot replace current matches.
    const request = useFetch(search, { immediate: false }).get().json<TeamCandidateSearch>();
    searchRequest.value = request;
    onCleanup(() => request.abort());
    void request.execute();
});
function execute() {
    return searchRequest.value?.execute();
}
const searching = computed(() => query.value !== debouncedQuery.value || isFetching.value);
const options = computed(() => {
    if (query.value.trim().length < 2 || searching.value || searchError.value) {
        return [];
    }
    const selected = new Set([creator.id, ...members.value.map((person) => person.id)]);
    return (data.value?.results ?? []).filter((person) => !selected.has(person.id));
});
const searchStatus = computed(() => {
    if (members.value.length >= 100) {
        return t("peopleDirectory.createTeam.limit");
    }
    if (query.value.trim().length < 2) {
        return "";
    }
    if (searching.value) {
        return t("peopleDirectory.createTeam.searching");
    }
    if (searchError.value) {
        return "";
    }
    return options.value.length ? t("peopleDirectory.createTeam.results", { count: options.value.length }) : t("peopleDirectory.createTeam.noMatches");
});
const submitting = ref(false);
const createdTeam = ref<TeamPerspectiveSummary | null>(null);
const submitError = ref("");
const requestId = globalThis.crypto.randomUUID();
const canCreate = computed(() => !!name.value.trim() && name.value.trim().length <= 255 && members.value.length > 0 && !submitting.value && showDetails.value);

watch(candidate, async (person) => {
    if (!person || submitting.value || members.value.length >= 100) {
        return;
    }
    if (person.id !== creator.id && !members.value.some((member) => member.id === person.id)) {
        members.value.push(person);
    }
    await nextTick();
    candidate.value = null;
    query.value = "";
});

function removeMember(id: string) {
    members.value = members.value.filter((member) => member.id !== id);
}

function useSuggestion(suggestion: TeamRecommendation) {
    if (submitting.value) {
        return;
    }
    if (!name.value.trim()) {
        name.value = suggestion.name;
    }
    const selected = new Set([creator.id, ...members.value.map((person) => person.id)]);
    for (const person of suggestion.members) {
        if (person.candidate) {
            if (!selected.has(person.candidate.id) && members.value.length < 100) {
                members.value.push(person.candidate);
                selected.add(person.candidate.id);
            }
        } else if (!unavailableMembers.value.some((member) => member.id === person.id)) {
            unavailableMembers.value.push(person);
        }
    }
    void reviewTeam();
}

async function createTeam() {
    if (!canCreate.value) {
        return;
    }
    submitting.value = true;
    submitError.value = "";
    const payload: TeamCreateRequest = { request_id: requestId, name: name.value.trim(), member_ids: members.value.map((member) => member.id) };
    try {
        const { data: team, error } = await useFetch(createUrl).post(payload).json<TeamPerspectiveSummary>();
        if (error.value || !team.value) {
            submitError.value = error.value instanceof Error ? error.value.message : t("peopleDirectory.createTeam.createError");
            submitting.value = false;
            return;
        }
        createdTeam.value = team.value;
        emit("created", team.value);
    } catch {
        submitError.value = t("peopleDirectory.createTeam.createError");
        submitting.value = false;
    }
}
</script>
